import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import heroImage from '../assets/r1.avif';
import claimImage from '../assets/r2.avif';
import FAQSection from './FAQSection';
import ConsultationCTA from '../components/ConsultationCTA';

const fadeIn = {
  hidden: { opacity: 0, y: 50 },
  visible: (i = 1) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.2, duration: 0.8, ease: 'easeOut' }
  })
};

const EligibilityCheck = () => {
  // Structured Data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "AI-Powered Insurance Eligibility Verification Services",
    "provider": {
      "@type": "MedicalBusiness",
      "name": "MDeRCM",
      "url": "https://mdercm.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Five Greentree Centre, 525 Route 73 North, Suite 104",
        "addressLocality": "Marlton",
        "addressRegion": "NJ",
        "postalCode": "08053",
        "addressCountry": "US"
      }
    },
    "description": "Automated healthcare revenue cycle management services with real-time insurance eligibility verification. Best revenue cycle management companies offering medical billing outsourcing services.",
    "areaServed": "United States",
    "serviceType": "Healthcare Revenue Cycle Management Services",
    "url": "https://mdercm.com/healthcare/ai/eligibility-check",
    "offers": {
      "@type": "Offer",
      "description": "AI-powered insurance verification and eligibility checking for healthcare providers"
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://mdercm.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Healthcare AI Solutions",
        "item": "https://mdercm.com/healthcare/ai"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Insurance Eligibility Check",
        "item": "https://mdercm.com/healthcare/ai/eligibility-check"
      }
    ]
  };

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags - SEO Optimized */}
        <title>Insurance Eligibility Verification | AI RCM | MDeRCM</title>
        <meta 
          name="title" 
          content="AI Insurance Eligibility Verification | Medical Billing Outsourcing Services | MDeRCM" 
        />
        <meta
          name="description"
          content="Real-time insurance eligibility verification with AI. Reduce claim denials from eligibility errors, verify coverage instantly before patient visits."
        />
        
        {/* SEO Keywords */}
        <meta 
          name="keywords" 
          content="healthcare revenue cycle management services, medical revenue cycle management services, outsourced revenue cycle management services, insurance eligibility verification, medical billing outsourcing services, revenue cycle management consulting services, best revenue cycle management companies, healthcare cost reduction strategies, revenue cycle management services usa" 
        />
        
        {/* Open Graph Tags */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mdercm.com/healthcare/ai/eligibility-check" />
        <meta property="og:title" content="AI Insurance Eligibility Verification | Healthcare RCM Services | MDeRCM" />
        <meta property="og:description" content="Best revenue cycle management companies offering AI-powered insurance verification. Reduce denials 70%, improve healthcare cost reduction with expert medical billing outsourcing services." />
        <meta property="og:image" content="https://mdercm.com/hero-bg.jpg" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://mdercm.com/healthcare/ai/eligibility-check" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      {/* Breadcrumb Navigation for SEO */}
      <nav aria-label="Breadcrumb" style={{ padding: '20px 5%', backgroundColor: '#f7f9fb' }}>
        <ol style={{ display: 'flex', listStyle: 'none', padding: 0, margin: 0, fontSize: '14px', color: '#555' }}>
          <li>
            <Link to="/" style={{ color: '#F26522', textDecoration: 'none' }}>
              Healthcare Revenue Cycle Management Services
            </Link>
          </li>
          <li style={{ margin: '0 10px' }}>/</li>
          <li>
            <Link to="/healthcare/ai" style={{ color: '#F26522', textDecoration: 'none' }}>
              AI Solutions
            </Link>
          </li>
          <li style={{ margin: '0 10px' }}>/</li>
          <li style={{ color: '#001F2B', fontWeight: '600' }}>Insurance Eligibility Check</li>
        </ol>
      </nav>

      {/* HERO SECTION */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        style={{
          backgroundImage: `linear-gradient(to right, rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.6)), url(${heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '80px 5%',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: '#ffffff',
        }}
      >
        <div style={{ flex: '1 1 100%', maxWidth: '600px', zIndex: 2 }}>
          <motion.h1 style={{ fontSize: 'clamp(28px, 5vw, 46px)', fontWeight: 700, marginBottom: '20px', lineHeight: 1.3 }}>
            Instant Insurance Validation with AI-Powered Healthcare Revenue Cycle Management Services
          </motion.h1>
          <motion.p style={{ fontSize: 'clamp(15px, 2vw, 19px)', marginBottom: '30px', color: '#dddddd', lineHeight: 1.6 }}>
            Transform your <strong>medical billing outsourcing services</strong> with MDeRCM's intelligent insurance verification system. As one of the <strong>best revenue cycle management companies</strong> in the USA, we provide <strong>outsourced revenue cycle management services</strong> that reduce denials by 70% and accelerate revenue recovery. Experience proven <strong>healthcare cost reduction strategies</strong> with our comprehensive <strong>revenue cycle management consulting services</strong>.
          </motion.p>

          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '30px',
            marginBottom: '30px'
          }}>
            {[
              ['98%', 'coverage validation accuracy'],
              ['70%', 'fewer eligibility-related denials'],
              ['20x', 'faster than manual verification']
            ].map(([percent, text], i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeIn}
                style={{
                  flex: '1 1 160px',
                  maxWidth: '220px',
                  textAlign: 'center'
                }}
              >
                <div style={{ fontSize: '36px', fontWeight: '700', color: '#F26522' }}>{percent}</div>
                <div style={{ fontSize: '16px', whiteSpace: 'pre-line', color: '#ffffff' }}>{text}</div>
              </motion.div>
            ))}
          </div>

          <motion.div style={{ marginTop: '30px' }}>
            <Link 
              to="/free-trial"
              style={{
                display: 'inline-block',
                padding: '15px 35px',
                backgroundColor: '#F26522',
                color: '#fff',
                borderRadius: '30px',
                fontSize: '16px',
                fontWeight: '700',
                textDecoration: 'none',
                boxShadow: '0 6px 20px rgba(242, 101, 34, 0.4)',
                transition: 'all 0.3s ease'
              }}
            >
              Start Free Trial - Best RCM Services
            </Link>
          </motion.div>
        </div>
      </motion.div>

      {/* SEO-RICH INTRO SECTION */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
        style={{ padding: '80px 5%', backgroundColor: '#ffffff' }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: 'clamp(26px, 4vw, 38px)',
            fontWeight: '700',
            color: '#001F2B',
            marginBottom: '30px',
            textAlign: 'center'
          }}>
            Leading Healthcare Revenue Cycle Management Services for Hospitals and Clinics
          </h2>
          
          <div style={{ fontSize: '17px', lineHeight: '1.8', color: '#333', marginBottom: '40px' }}>
            <p style={{ marginBottom: '20px' }}>
              <Link to="/" style={{ color: '#F26522', fontWeight: '600', textDecoration: 'none' }}>
                MDeRCM's healthcare revenue cycle management services
              </Link> combine cutting-edge AI technology with expert <strong>medical billing and coding outsourcing</strong> to deliver unmatched results. Our <strong>revenue cycle management services for hospitals</strong> and clinics across the USA help you understand <strong>how to improve revenue cycle management</strong> while reducing operational costs by 30-50%.
            </p>
            <p style={{ marginBottom: '20px' }}>
              As specialists among <strong>revenue cycle management services companies</strong>, we offer comprehensive <strong>outsourced revenue cycle management services</strong> that include insurance eligibility verification, claims processing, denial management, and accounts receivable optimization. Our proven <strong>healthcare cost reduction consulting</strong> strategies have helped thousands of healthcare providers improve cash flow and reduce administrative burden.
            </p>
            <p>
              Join the <strong>best revenue cycle management companies</strong> clients who trust our <strong>medical revenue cycle management services</strong> for superior results. Whether you need <strong>hospital revenue cycle management</strong> or specialized <strong>medical billing outsourcing companies</strong> support, MDeRCM delivers enterprise-grade solutions tailored to your practice size and specialty.
            </p>
          </div>
        </div>
      </motion.div>

      {/* CLAIM SUBMISSION SECTION with Internal Links */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
        style={{ padding: '80px 5%', backgroundColor: '#f7f9fb' }}
      >
        <h2 style={{
          textAlign: 'center',
          fontSize: 'clamp(24px, 4vw, 36px)',
          fontWeight: '700',
          color: '#001F2B',
          marginBottom: '50px'
        }}>
          Effortless Claims with AI-Powered Medical Billing Outsourcing Services
        </h2>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '40px',
          flexDirection: 'row'
        }}>
          <div style={{
            flex: '1 1 500px',
            minWidth: '280px',
            maxWidth: '600px'
          }}>
            <h3 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '20px', color: '#001F2B' }}>
              Built-in AI Intelligence for Clean Claims and Faster Approvals
            </h3>
            <p style={{ fontSize: '16px', lineHeight: '1.7', color: '#333', marginBottom: '20px' }}>
              MDeRCM's claim engine scans every claim the moment it's created—cross-checking payer guidelines, validating required fields, and catching potential rejections before submission. Our <Link to="/healthcare/ai/policy-status-verification" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>Policy Status Verification AI</Link> ensures clean claims, faster reimbursements, and reduced manual corrections.
            </p>
            <p style={{ fontSize: '16px', lineHeight: '1.7', color: '#333' }}>
              Explore our complete suite of <strong>revenue cycle management consulting services</strong>:
            </p>
            <ul style={{ fontSize: '16px', lineHeight: '1.8', color: '#333', marginTop: '15px' }}>
              <li>
                <Link to="/healthcare/ai/prior-authorization" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>
                  Prior Authorization Management
                </Link> - Accelerate approvals by 90%
              </li>
              <li>
                <Link to="/healthcare/ai/denial-management" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>
                  Denial Management Solutions
                </Link> - Reduce denials by 70%
              </li>
              <li>
                <Link to="/healthcare/ai/payment-posting" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>
                  Automated Payment Posting
                </Link> - 50x faster processing
              </li>
              <li>
                <Link to="/healthcare/ai/accounts-receivable" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>
                  AR Management Services
                </Link> - 60% faster collections
              </li>
            </ul>
          </div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.5 }}
            style={{
              flex: '1 1 400px',
              minWidth: '280px',
              maxWidth: '500px',
              textAlign: 'center'
            }}
          >
            <img
              src={claimImage}
              alt="AI-powered healthcare revenue cycle management services for medical billing outsourcing"
              style={{
                width: '100%',
                borderRadius: '20px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
              }}
            />
          </motion.div>
        </div>
      </motion.div>

      {/* AI AGENTS SECTION */}
      <div style={{ padding: '80px 5%', backgroundColor: '#ffffff' }}>
        <motion.h2
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(24px, 4vw, 36px)',
            fontWeight: '800',
            color: '#001F2B',
            marginBottom: '50px',
            letterSpacing: '1px'
          }}
        >
          What Can Our Insurance Verification AI Agent Do? - Medical Revenue Cycle Management Services
        </motion.h2>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '24px'
        }}>
          {[
            ["🔍", "Automated Data Retrieval", "The AI agent connects with EHRs, payer systems, and billing portals to automatically pull accurate patient and insurance data—eliminating manual entry and reducing admin overhead. Part of our comprehensive medical billing outsourcing services."],
            ["⚡", "Instant Eligibility Confirmation", "Before the patient even checks in, the agent verifies insurance status, benefits, co-pays, and deductible limits in real-time—then syncs the data back to your platform for seamless intake. Best healthcare revenue cycle management services."],
            ["🚫", "Coverage Issue Alerts", "From inactive policies to mismatched IDs, the AI flags eligibility problems as they arise—empowering your team to correct errors early and avoid costly rejections. Proven healthcare cost reduction strategies."],
            ["🛡️", "Smart Fraud Detection", "With built-in anomaly detection, the system identifies suspicious patterns like duplicate claims or altered coverage records—protecting your practice and ensuring compliance with our revenue cycle management consulting services."],
            ["📢", "Live Updates & Patient Alerts", "Get notified the moment a policy changes, expires, or requires approval. The AI also sends alerts to patients for out-of-pocket costs or missing information before their visit."],
            ["🌐", "Universal Payer Support", "No matter the insurer, the agent adapts—handling different portals, formats, and coverage rules to deliver consistent, verified eligibility checks across all payers. Best revenue cycle management companies solution."]
          ].map(([icon, title, desc], i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15, duration: 0.7 }}
              style={{
                width: '100%',
                maxWidth: '300px',
                backgroundColor: '#f7f9fb',
                padding: '24px',
                borderRadius: '16px',
                boxShadow: '0 12px 30px rgba(0,0,0,0.08)',
                textAlign: 'center'
              }}
            >
              <div style={{ fontSize: '36px', marginBottom: '12px' }}>{icon}</div>
              <h4 style={{ fontSize: '17px', fontWeight: '700', marginBottom: '10px', color: '#001F2B' }}>{title}</h4>
              <p style={{ fontSize: '15px', color: '#555', lineHeight: '1.5' }}>{desc}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* BENEFITS SECTION with SEO Keywords */}
      <div style={{ padding: '80px 5%', backgroundColor: '#f7f9fb' }}>
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{
            textAlign: 'center',
            fontSize: 'clamp(24px, 4vw, 36px)',
            fontWeight: '800',
            color: '#001F2B',
            marginBottom: '30px'
          }}
        >
          Key Benefits of Outsourced Revenue Cycle Management Services
        </motion.h2>

        <p style={{
          textAlign: 'center',
          fontSize: '17px',
          color: '#555',
          maxWidth: '900px',
          margin: '0 auto 50px',
          lineHeight: '1.8'
        }}>
          Discover how our <strong>revenue cycle management services for hospitals</strong> and clinics deliver measurable results. Learn <strong>how to improve revenue cycle management</strong> with proven <strong>healthcare cost reduction consulting</strong> from the <strong>best revenue cycle management companies</strong> in the <strong>revenue cycle management services USA</strong> market.
        </p>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '30px'
        }}>
          {[
            ["⚙️", "Faster Processing, Less Manual Load", "Handles checks, validations, and claim data reviews in real-time—cutting hours of manual work and reducing pressure on your team. Best medical billing outsourcing companies solution."],
            ["💸", "Reduced Operational Costs", "By eliminating repetitive tasks and boosting efficiency, our outsourced revenue cycle management services lower staffing costs and improve your bottom line with proven healthcare cost reduction strategies."],
            ["⏱️", "Always-On Automation", "The agent works 24/7—verifying insurance details instantly without delays, helping teams maintain round-the-clock readiness. Part of our comprehensive medical revenue cycle management services."],
            ["📈", "Scalable with Zero Bottlenecks", "Whether you handle 100 or 10,000 verifications, the system scales without needing extra staff or infrastructure changes. Revenue cycle management consulting services that grow with you."],
            ["⭐", "Smoother Patient Experience", "Accelerates front desk workflows and minimizes wait times, helping patients move from check-in to care with less friction. Hospital revenue cycle management excellence."],
            ["📊", "Insightful Data & Reporting", "Delivers real-time metrics and predictive alerts, empowering smarter decisions and continuous revenue cycle improvement. Revenue cycle management services companies trust our analytics."]
          ].map(([icon, title, desc], i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              style={{
                flex: '1 1 280px',
                maxWidth: '400px',
                backgroundColor: '#ffffff',
                padding: '24px',
                borderRadius: '12px',
                boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
                textAlign: 'left'
              }}
            >
              <div style={{ fontSize: '28px', marginBottom: '12px' }}>{icon}</div>
              <h4 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '10px', color: '#001F2B' }}>{title}</h4>
              <p style={{ fontSize: '15px', color: '#444', lineHeight: '1.6' }}>{desc}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Related Services Section - Internal Linking */}
      <div style={{ padding: '80px 5%', backgroundColor: '#ffffff' }}>
        <h2 style={{
          textAlign: 'center',
          fontSize: 'clamp(24px, 4vw, 36px)',
          fontWeight: '700',
          color: '#001F2B',
          marginBottom: '50px'
        }}>
          Explore Our Complete Healthcare Revenue Cycle Management Services
        </h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '30px',
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          <style>
            {`
              @media (max-width: 1200px) {
                div[style*="repeat(4, 1fr)"] {
                  grid-template-columns: repeat(3, 1fr) !important;
                }
              }
              @media (max-width: 900px) {
                div[style*="repeat(4, 1fr)"] {
                  grid-template-columns: repeat(2, 1fr) !important;
                }
              }
              @media (max-width: 600px) {
                div[style*="repeat(4, 1fr)"] {
                  grid-template-columns: 1fr !important;
                }
              }
            `}
          </style>
          {[
            {
              title: "Policy Status Verification",
              desc: "Real-time insurance validation with 99% accuracy",
              link: "/healthcare/ai/policy-status-verification"
            },
            {
              title: "Prior Authorization",
              desc: "Accelerate approvals by 90% with AI automation",
              link: "/healthcare/ai/prior-authorization"
            },
            {
              title: "Patient Intake & Reminders",
              desc: "Automated payment reminders and collections",
              link: "/healthcare/ai/patient-intake"
            },
            {
              title: "Denial Management",
              desc: "Reduce denials by 70% with smart recovery",
              link: "/healthcare/ai/denial-management"
            },
            {
              title: "Payment Posting",
              desc: "50x faster automated payment reconciliation",
              link: "/healthcare/ai/payment-posting"
            },
            {
              title: "Accounts Receivable",
              desc: "Accelerate collections with AI-driven AR management",
              link: "/healthcare/ai/accounts-receivable"
            },
            {
              title: "Compliance Management",
              desc: "Stay audit-ready with continuous compliance monitoring",
              link: "/healthcare/ai/compliance-agent"
            },
            {
              title: "Insurance Contract Repricing",
              desc: "Optimize contracts with AI-powered analysis",
              link: "/healthcare/ai/InsuranceContract-Repricing"
            }
          ].map((service, i) => (
            <Link
              key={i}
              to={service.link}
              style={{
                textDecoration: 'none',
                backgroundColor: '#f7f9fb',
                padding: '30px',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease',
                display: 'block'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-5px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(242, 101, 34, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.08)';
              }}
            >
              <h3 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#001F2B',
                marginBottom: '10px'
              }}>
                {service.title}
              </h3>
              <p style={{
                fontSize: '15px',
                color: '#555',
                lineHeight: '1.6',
                marginBottom: '15px'
              }}>
                {service.desc}
              </p>
              <span style={{
                color: '#F26522',
                fontWeight: '600',
                fontSize: '15px'
              }}>
                Learn More →
              </span>
            </Link>
          ))}
        </div>
      </div>

      {/* SEO Footer Section */}
      <div style={{ padding: '60px 5%', backgroundColor: '#001F2B', color: '#ffffff' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{
            fontSize: 'clamp(24px, 4vw, 32px)',
            fontWeight: '700',
            marginBottom: '25px',
            color: '#F26522'
          }}>
            Why Choose MDeRCM for Healthcare Revenue Cycle Management Services?
          </h2>
          <p style={{
            fontSize: '16px',
            lineHeight: '1.8',
            color: '#dddddd',
            maxWidth: '900px',
            margin: '0 auto 30px'
          }}>
            As one of the <strong>best revenue cycle management companies</strong>, MDeRCM specializes in <strong>medical billing and coding outsourcing</strong>, <strong>hospital revenue cycle management</strong>, and comprehensive <strong>revenue cycle management consulting services</strong>. Our proven <strong>healthcare cost reduction strategies</strong> help you understand <strong>how to improve revenue cycle management</strong> while reducing costs by 30-50%. Join thousands of healthcare providers across the <strong>revenue cycle management services USA</strong> market who trust our <strong>outsourced revenue cycle management services</strong>.
          </p>
          <div style={{ marginTop: '30px' }}>
            <Link
              to="/"
              style={{
                display: 'inline-block',
                padding: '15px 35px',
                backgroundColor: '#F26522',
                color: '#fff',
                borderRadius: '30px',
                fontSize: '16px',
                fontWeight: '700',
                textDecoration: 'none',
                boxShadow: '0 6px 20px rgba(242, 101, 34, 0.4)',
                marginRight: '15px',
                marginBottom: '15px'
              }}
            >
              Explore All RCM Services
            </Link>
            <Link
              to="/free-trial"
              style={{
                display: 'inline-block',
                padding: '15px 35px',
                backgroundColor: 'transparent',
                color: '#fff',
                border: '2px solid #F26522',
                borderRadius: '30px',
                fontSize: '16px',
                fontWeight: '700',
                textDecoration: 'none',
                marginBottom: '15px'
              }}
            >
              Start Free Trial
            </Link>
          </div>
        </div>
      </div>

      <FAQSection />
      <ConsultationCTA />
    </>
  );
};

export default EligibilityCheck;